import type { FishGroupOperationSummary, FishGroupTransferEvent } from '../historyTypes';
import type {
    EditableFishGroupField,
    FishGroupMetadataChange,
    FishGroupOperationSummaryDto,
    FishGroupTransferEventDto,
} from '../types';

import { asEventType, asOperationType } from './enumGuards';

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export function mapFishGroupEventsToDomain(
    dtos: FishGroupTransferEventDto[]
): FishGroupTransferEvent[] {
    return dtos.map(mapFishGroupEventToDomain);
}

function mapFishGroupEventToDomain(dto: FishGroupTransferEventDto): FishGroupTransferEvent {
    const eventData = (dto.eventData as Record<string, unknown> | undefined) ?? null;
    return {
        id: dto.id,
        operationId: dto.operationId ?? null,
        fishGroupId: dto.fishGroupId,
        eventType: asEventType(dto.eventType),
        eventDate: dto.eventDate,
        fishCountBefore: dto.fishCountBefore ?? null,
        fishCountAfter: dto.fishCountAfter ?? null,
        cageId: dto.cageId ?? null,
        operatorId: dto.operatorId,
        notes: dto.notes ?? null,
        eventData,
        operation: dto.operation ? mapOperationSummaryToDomain(dto.operation) : null,
        metadataChanges: mapMetadataChangesFromEventData(eventData),
        operatorName: mapOperatorNameFromEventData(eventData),
    };
}

const EDITABLE_FIELDS: readonly EditableFishGroupField[] = [
    'name',
    'notes',
    'initialPopulationSize',
];

function isMetadataChange(value: unknown): value is FishGroupMetadataChange {
    if (typeof value !== 'object' || value === null) return false;
    const candidate = value as Record<string, unknown>;
    const isStoredValue = (raw: unknown) =>
        raw === null || typeof raw === 'string' || typeof raw === 'number';
    return (
        (EDITABLE_FIELDS as readonly unknown[]).includes(candidate.field) &&
        isStoredValue(candidate.from) &&
        isStoredValue(candidate.to)
    );
}

/**
 * Narrow the `changes` array out of a `metadata_updated` event's payload.
 *
 * `eventData` is an untyped jsonb bag, so every level is guarded and malformed
 * entries are dropped rather than thrown on: a single bad row must not take
 * down the whole events list. An empty array means "nothing renderable here",
 * which callers should treat as a reason to fall back to the raw payload view.
 */
function mapMetadataChangesFromEventData(
    eventData: Record<string, unknown> | null
): FishGroupMetadataChange[] {
    const raw = eventData?.changes;
    if (!Array.isArray(raw)) return [];
    return raw.filter(isMetadataChange);
}

/** Read the operator name snapshotted into a correction event's payload. */
function mapOperatorNameFromEventData(eventData: Record<string, unknown> | null): string | null {
    const name = eventData?.operatorName;
    return typeof name === 'string' && name.length > 0 ? name : null;
}

function mapOperationSummaryToDomain(dto: FishGroupOperationSummaryDto): FishGroupOperationSummary {
    return {
        id: dto.id,
        operationType: asOperationType(dto.operationType),
        operationDate: dto.operationDate,
        reason: dto.reason ?? null,
        notes: dto.notes ?? null,
        operatorId: dto.operatorId,
        revertsOperationId: dto.revertsOperationId ?? null,
        revertedByOperationId: dto.revertedByOperationId ?? null,
    };
}
